import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Plus, Users, Trash2, Pencil, Eye, Filter, Search,
  ArrowLeft, ChevronRight, AlertTriangle, Layers, X
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const RISK_OPTIONS = [
  { value: 1, label: 'Critical', color: 'bg-red-600' },
  { value: 2, label: 'High Risk', color: 'bg-orange-500' },
  { value: 3, label: 'Monitor', color: 'bg-yellow-500' },
  { value: 4, label: 'Low Risk', color: 'bg-blue-500' },
  { value: 5, label: 'Stable', color: 'bg-green-500' },
];

const INSURANCE_OPTIONS = ['Blue Cross Blue Shield', 'UnitedHealthcare', 'Aetna', 'Cigna', 'Humana', 'Medicare', 'Medicaid', 'Kaiser Permanente'];
const CONDITION_OPTIONS = ['Diabetes Type 2', 'Hypertension', 'Obesity', 'COPD', 'Heart Failure', 'Chronic Kidney Disease', 'Asthma', 'Depression', 'Anxiety'];
const STATUS_OPTIONS = ['Active', 'Inactive', 'Monitoring'];

const emptyFilters = {
  risk_scores: [],
  age_min: null,
  age_max: null,
  insurance_providers: [],
  conditions: [],
  dts_min: null,
  dts_max: null,
  mood_min: null,
  mood_max: null,
  states: [],
  statuses: [],
};

const CohortBuilder = () => {
  const navigate = useNavigate();
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [showMembers, setShowMembers] = useState(null);
  const [cohortMembers, setCohortMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Builder state
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [filters, setFilters] = useState({ ...emptyFilters });
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadCohorts();
  }, []);

  const loadCohorts = async () => {
    try {
      setLoading(true);
      const res = await api.getCohorts();
      setCohorts(res.data);
    } catch {
      toast.error('Failed to load cohorts');
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = useCallback(async () => {
    // Clean filters: remove empty arrays and nulls
    const cleanFilters = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (Array.isArray(v) && v.length > 0) cleanFilters[k] = v;
      else if (v !== null && v !== '' && !Array.isArray(v)) cleanFilters[k] = Number(v);
    });

    try {
      setPreviewLoading(true);
      const res = await api.previewCohort(cleanFilters);
      setPreview(res.data);
    } catch {
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (showBuilder) {
      const timer = setTimeout(() => loadPreview(), 400);
      return () => clearTimeout(timer);
    }
  }, [filters, showBuilder, loadPreview]);

  const openBuilder = (cohort = null) => {
    if (cohort) {
      setEditingId(cohort.id);
      setName(cohort.name);
      setDescription(cohort.description || '');
      setFilters({ ...emptyFilters, ...(cohort.filters || {}) });
    } else {
      setEditingId(null);
      setName('');
      setDescription('');
      setFilters({ ...emptyFilters });
    }
    setPreview(null);
    setShowBuilder(true);
  };

  const closeBuilder = () => {
    setShowBuilder(false);
    setEditingId(null);
    setPreview(null);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a cohort name');
      return;
    }

    const cleanFilters = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (Array.isArray(v) && v.length > 0) cleanFilters[k] = v;
      else if (v !== null && v !== '' && !Array.isArray(v)) cleanFilters[k] = Number(v);
    });

    try {
      setSaving(true);
      if (editingId) {
        await api.updateCohort(editingId, { name: name.trim(), description: description.trim(), filters: cleanFilters });
        toast.success('Cohort updated');
      } else {
        await api.createCohort({ name: name.trim(), description: description.trim(), filters: cleanFilters });
        toast.success('Cohort created');
      }
      closeBuilder();
      await loadCohorts();
    } catch {
      toast.error('Failed to save cohort');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteCohort(id);
      toast.success('Cohort deleted');
      setDeleteConfirm(null);
      await loadCohorts();
    } catch {
      toast.error('Failed to delete cohort');
    }
  };

  const handleViewMembers = async (cohort) => {
    try {
      setMembersLoading(true);
      setShowMembers(cohort);
      const res = await api.getCohortMembers(cohort.id);
      setCohortMembers(res.data);
    } catch {
      toast.error('Failed to load members');
    } finally {
      setMembersLoading(false);
    }
  };

  const toggleArrayFilter = (key, value) => {
    setFilters((prev) => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };

  const getRiskLabel = (score) => {
    const opt = RISK_OPTIONS.find((r) => r.value === score);
    return opt ? opt.label : `Risk ${score}`;
  };

  const getRiskBadgeStyle = (score) => {
    if (score === 1) return 'bg-red-600 text-white';
    if (score === 2) return 'bg-orange-500 text-white';
    if (score === 3) return 'bg-yellow-500 text-white';
    if (score === 4) return 'bg-blue-500 text-white';
    return 'bg-green-500 text-white';
  };

  const activeFilterCount = Object.entries(filters).filter(([, v]) =>
    (Array.isArray(v) && v.length > 0) || (v !== null && v !== '' && !Array.isArray(v))
  ).length;

  // === MEMBERS VIEW ===
  if (showMembers) {
    return (
      <div className="space-y-4 fade-in" data-testid="cohort-members-view">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setShowMembers(null); setCohortMembers([]); }} data-testid="back-to-cohorts">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{showMembers.name}</h1>
            <p className="text-sm text-gray-500">{showMembers.description || 'No description'} &middot; {cohortMembers.length} member{cohortMembers.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {/* Active filters display */}
        {showMembers.filters && (
          <div className="flex flex-wrap gap-2">
            {showMembers.filters.risk_scores?.map((r) => (
              <Badge key={r} variant="secondary" className="text-xs">{getRiskLabel(r)}</Badge>
            ))}
            {showMembers.filters.insurance_providers?.map((p) => (
              <Badge key={p} variant="secondary" className="text-xs">{p}</Badge>
            ))}
            {showMembers.filters.conditions?.map((c) => (
              <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>
            ))}
            {showMembers.filters.dts_min != null && <Badge variant="secondary" className="text-xs">DTS &ge; {showMembers.filters.dts_min}</Badge>}
            {showMembers.filters.dts_max != null && <Badge variant="secondary" className="text-xs">DTS &le; {showMembers.filters.dts_max}</Badge>}
          </div>
        )}

        <Card>
          <CardContent className="pt-4">
            {membersLoading ? (
              <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="skeleton h-12"></div>)}</div>
            ) : cohortMembers.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p>No members match this cohort's filters</p>
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="cohort-members-table">
                <thead>
                  <tr className="text-gray-500 border-b border-gray-200 text-xs">
                    <th className="text-left py-2 font-medium">Name</th>
                    <th className="text-center py-2 font-medium">Risk Tier</th>
                    <th className="text-center py-2 font-medium">DTS</th>
                    <th className="text-center py-2 font-medium">Mood</th>
                    <th className="text-left py-2 font-medium">Insurance</th>
                    <th className="text-left py-2 font-medium">Conditions</th>
                    <th className="text-right py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cohortMembers.map((m) => (
                    <tr key={m.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/patients/${m.id}`)}>
                      <td className="py-2.5 font-medium">{m.first_name} {m.last_name}</td>
                      <td className="text-center py-2.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${getRiskBadgeStyle(m.risk_score)}`}>
                          {getRiskLabel(m.risk_score)}
                        </span>
                      </td>
                      <td className={`text-center py-2.5 font-medium ${m.digital_twin_score < 65 ? 'text-red-600' : ''}`}>{m.digital_twin_score}</td>
                      <td className={`text-center py-2.5 font-medium ${m.mood_score < 5 ? 'text-red-600' : ''}`}>{m.mood_score}/10</td>
                      <td className="py-2.5 text-gray-600">{m.insurance_provider || '—'}</td>
                      <td className="py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {(m.condition_tags || []).slice(0, 2).map((c) => (
                            <span key={c} className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded-full text-gray-600">{c}</span>
                          ))}
                        </div>
                      </td>
                      <td className="text-right py-2.5">
                        <Button size="sm" variant="ghost" className="text-xs text-purple-700" onClick={(e) => { e.stopPropagation(); navigate(`/patients/${m.id}`); }}>
                          View <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // === MAIN COHORT LIST ===
  return (
    <div className="space-y-6 fade-in" data-testid="cohort-builder-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="cohort-builder-title">
            Cohort Builder
          </h1>
          <p className="text-gray-500 text-sm mt-1">Create and manage population segments for tracking and analysis</p>
        </div>
        <Button onClick={() => openBuilder()} className="bg-purple-700 hover:bg-purple-800" data-testid="create-cohort-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Cohort
        </Button>
      </div>

      {/* Cohort Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-40"></div>)}
        </div>
      ) : cohorts.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Layers className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>No Cohorts Yet</h3>
            <p className="text-gray-500 text-sm mb-4">Create your first cohort to start segmenting your member population.</p>
            <Button onClick={() => openBuilder()} className="bg-purple-700 hover:bg-purple-800" data-testid="create-first-cohort-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create First Cohort
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cohorts.map((cohort) => (
            <Card key={cohort.id} className="hover:shadow-md transition-shadow" data-testid={`cohort-card-${cohort.id}`}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{cohort.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{cohort.description || 'No description'}</p>
                  </div>
                  <div className="flex items-center gap-1 ml-2 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openBuilder(cohort)} data-testid={`edit-cohort-${cohort.id}`}>
                      <Pencil className="w-3.5 h-3.5 text-gray-500" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteConfirm(cohort)} data-testid={`delete-cohort-${cohort.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-gray-500" />
                    </Button>
                  </div>
                </div>

                <div className="flex items-center gap-4 mb-3">
                  <div className="text-center">
                    <p className="text-xl font-bold text-purple-700" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{cohort.member_count}</p>
                    <p className="text-[10px] text-gray-500 uppercase">Members</p>
                  </div>
                  <div className="flex-1 flex flex-wrap gap-1">
                    {cohort.filters?.risk_scores?.map((r) => (
                      <span key={r} className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white ${RISK_OPTIONS.find((o) => o.value === r)?.color || 'bg-gray-400'}`}>
                        {getRiskLabel(r)}
                      </span>
                    ))}
                    {cohort.filters?.conditions?.slice(0, 2).map((c) => (
                      <span key={c} className="inline-block text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{c}</span>
                    ))}
                    {(cohort.filters?.conditions?.length || 0) > 2 && (
                      <span className="text-[10px] text-gray-400">+{cohort.filters.conditions.length - 2}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                  <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => handleViewMembers(cohort)} data-testid={`view-members-${cohort.id}`}>
                    <Eye className="w-3 h-3 mr-1" />
                    View Members
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => navigate('/analytics')} data-testid={`view-analytics-${cohort.id}`}>
                    <Filter className="w-3 h-3 mr-1" />
                    Analytics
                  </Button>
                </div>

                <p className="text-[10px] text-gray-400 mt-2">Created {new Date(cohort.created_at).toLocaleDateString()} by {cohort.created_by}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ===== COHORT BUILDER DIALOG ===== */}
      <Dialog open={showBuilder} onOpenChange={setShowBuilder}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cohort-builder-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {editingId ? 'Edit Cohort' : 'Create New Cohort'}
            </DialogTitle>
            <p className="text-sm text-gray-500">Define filters to segment your member population</p>
          </DialogHeader>

          <div className="space-y-5">
            {/* Name & Description */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Cohort Name *</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Seniors 65+ High Risk"
                  className="h-9"
                  data-testid="cohort-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Description</Label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description..."
                  className="h-9"
                  data-testid="cohort-description-input"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="border border-gray-200 rounded-xl p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-purple-700" />
                  <p className="text-sm font-semibold">Filter Criteria</p>
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="text-xs">{activeFilterCount} active</Badge>
                  )}
                </div>
                <Button variant="ghost" size="sm" className="text-xs text-gray-500" onClick={() => setFilters({ ...emptyFilters })} data-testid="clear-filters-btn">
                  Clear All
                </Button>
              </div>

              {/* Risk Tier */}
              <div>
                <Label className="text-xs font-medium text-gray-600">Risk Tier</Label>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {RISK_OPTIONS.map((opt) => {
                    const active = (filters.risk_scores || []).includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        onClick={() => toggleArrayFilter('risk_scores', opt.value)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          active ? 'text-white border-transparent ' + opt.color : 'text-gray-600 border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                        data-testid={`filter-risk-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Age Range */}
              <div>
                <Label className="text-xs font-medium text-gray-600">Age Range</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <Input
                    type="number"
                    placeholder="Min"
                    className="h-8 w-24 text-xs"
                    value={filters.age_min || ''}
                    onChange={(e) => setFilters({ ...filters, age_min: e.target.value ? Number(e.target.value) : null })}
                    data-testid="filter-age-min"
                  />
                  <span className="text-gray-400 text-xs">to</span>
                  <Input
                    type="number"
                    placeholder="Max"
                    className="h-8 w-24 text-xs"
                    value={filters.age_max || ''}
                    onChange={(e) => setFilters({ ...filters, age_max: e.target.value ? Number(e.target.value) : null })}
                    data-testid="filter-age-max"
                  />
                </div>
              </div>

              {/* DTS Range */}
              <div>
                <Label className="text-xs font-medium text-gray-600">Digital Twin Score Range</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <Input
                    type="number"
                    placeholder="Min (0)"
                    className="h-8 w-24 text-xs"
                    value={filters.dts_min ?? ''}
                    onChange={(e) => setFilters({ ...filters, dts_min: e.target.value ? Number(e.target.value) : null })}
                    data-testid="filter-dts-min"
                  />
                  <span className="text-gray-400 text-xs">to</span>
                  <Input
                    type="number"
                    placeholder="Max (100)"
                    className="h-8 w-24 text-xs"
                    value={filters.dts_max ?? ''}
                    onChange={(e) => setFilters({ ...filters, dts_max: e.target.value ? Number(e.target.value) : null })}
                    data-testid="filter-dts-max"
                  />
                </div>
              </div>

              {/* Mood Range */}
              <div>
                <Label className="text-xs font-medium text-gray-600">Mood Score Range</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <Input
                    type="number"
                    placeholder="Min (1)"
                    className="h-8 w-24 text-xs"
                    value={filters.mood_min ?? ''}
                    onChange={(e) => setFilters({ ...filters, mood_min: e.target.value ? Number(e.target.value) : null })}
                    data-testid="filter-mood-min"
                  />
                  <span className="text-gray-400 text-xs">to</span>
                  <Input
                    type="number"
                    placeholder="Max (10)"
                    className="h-8 w-24 text-xs"
                    value={filters.mood_max ?? ''}
                    onChange={(e) => setFilters({ ...filters, mood_max: e.target.value ? Number(e.target.value) : null })}
                    data-testid="filter-mood-max"
                  />
                </div>
              </div>

              {/* Insurance */}
              <div>
                <Label className="text-xs font-medium text-gray-600">Insurance Provider</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {INSURANCE_OPTIONS.map((ins) => {
                    const active = (filters.insurance_providers || []).includes(ins);
                    return (
                      <button
                        key={ins}
                        onClick={() => toggleArrayFilter('insurance_providers', ins)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${
                          active ? 'bg-purple-100 border-purple-300 text-purple-800' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                        data-testid={`filter-ins-${ins.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {active && <span className="mr-1">&#10003;</span>}{ins}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Conditions */}
              <div>
                <Label className="text-xs font-medium text-gray-600">Conditions</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {CONDITION_OPTIONS.map((cond) => {
                    const active = (filters.conditions || []).includes(cond);
                    return (
                      <button
                        key={cond}
                        onClick={() => toggleArrayFilter('conditions', cond)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${
                          active ? 'bg-purple-100 border-purple-300 text-purple-800' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                        data-testid={`filter-cond-${cond.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {active && <span className="mr-1">&#10003;</span>}{cond}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Status */}
              <div>
                <Label className="text-xs font-medium text-gray-600">Status</Label>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {STATUS_OPTIONS.map((s) => {
                    const active = (filters.statuses || []).includes(s);
                    return (
                      <button
                        key={s}
                        onClick={() => toggleArrayFilter('statuses', s)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          active ? 'bg-purple-700 text-white border-purple-700' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}
                        data-testid={`filter-status-${s.toLowerCase()}`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-purple-700" />
                  <p className="text-sm font-semibold">Live Preview</p>
                </div>
                {preview && (
                  <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                    {preview.count} member{preview.count !== 1 ? 's' : ''} matched
                  </Badge>
                )}
              </div>

              {previewLoading ? (
                <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="skeleton h-8"></div>)}</div>
              ) : preview ? (
                preview.count === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No members match these filters</p>
                    <p className="text-xs text-gray-400 mt-1">Try broadening your criteria</p>
                  </div>
                ) : (
                  <div>
                    <table className="w-full text-xs" data-testid="preview-table">
                      <thead>
                        <tr className="text-gray-500 border-b border-gray-100">
                          <th className="text-left py-1.5 font-medium">Name</th>
                          <th className="text-center py-1.5 font-medium">Risk Tier</th>
                          <th className="text-center py-1.5 font-medium">DTS</th>
                          <th className="text-left py-1.5 font-medium">Insurance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.preview.map((m) => (
                          <tr key={m.id} className="border-b border-gray-50">
                            <td className="py-1.5 font-medium text-gray-900">{m.first_name} {m.last_name}</td>
                            <td className="text-center py-1.5">
                              <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white ${getRiskBadgeStyle(m.risk_score)}`}>
                                {getRiskLabel(m.risk_score)}
                              </span>
                            </td>
                            <td className="text-center py-1.5">{m.digital_twin_score}</td>
                            <td className="py-1.5 text-gray-600">{m.insurance_provider || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {preview.count > 10 && (
                      <p className="text-xs text-gray-400 mt-2 text-center">Showing 10 of {preview.count} matching members</p>
                    )}
                  </div>
                )
              ) : (
                <p className="text-xs text-gray-400 text-center py-4">Adjust filters above to preview matching members</p>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={closeBuilder} data-testid="cancel-cohort-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !name.trim()} className="bg-purple-700 hover:bg-purple-800" data-testid="save-cohort-btn">
              {saving ? 'Saving...' : editingId ? 'Update Cohort' : 'Create Cohort'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Delete Cohort</DialogTitle>
            <p className="text-sm text-gray-500">This action cannot be undone</p>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm?.id)} data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CohortBuilder;
